import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import useApiFetch from '@/hooks/useApiFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import financeCategoriesService from '@/services/financeCategories';
import financeTransactionsService from '@/services/financeTransactions';
import financeVendorsService from '@/services/financeVendors';
import { FetchApiIds } from '@/types';
import { FxTransaction, FxVendor, FxCategory, FxReviewStatus } from '@/types/api/finance';

function generateCsv(transactions: FxTransaction[]): string {
  const header = 'Date,Description,Vendor,Category,Type,Amount,Currency';
  const rows = transactions.map((tx) => {
    const vendor = tx.vendor?.displayName || tx.vendor?.name || '';
    const category = tx.category?.name || '';
    const desc = tx.description.replace(/"/g, '""');
    return `${tx.date},"${desc}","${vendor}","${category}",${tx.type},${tx.amount},${tx.currency}`;
  });

  // Group by category for subtotals
  const byCat: Record<string, number> = {};
  transactions.forEach((tx) => {
    const cat = tx.category?.name || 'Uncategorized';
    byCat[cat] = (byCat[cat] ?? 0) + tx.amount;
  });

  const subtotals = Object.entries(byCat)
    .sort(([, a], [, b]) => b - a)
    .map(([cat, total]) => `,,,"${cat} — Subtotal",,${total.toFixed(2)},`);

  const grandTotal = transactions.reduce((sum, tx) => sum + tx.amount, 0);

  return [
    header,
    ...rows,
    '',
    ',,,"=== SUBTOTALS BY CATEGORY ===",,',
    ...subtotals,
    '',
    `,,,"GRAND TOTAL",,${grandTotal.toFixed(2)},`,
  ].join('\n');
}

type UseReviewQueueReturn = {
  transactions: FxTransaction[];
  vendors: FxVendor[];
  categories: FxCategory[];
  isLoading: boolean;
  counts: { all: number; pending: number; reviewed: number; approved: number };
  activeTab: string;
  setActiveTab: (tab: string) => void;
  approveOne: (id: string) => Promise<void>;
  markReviewed: (id: string) => Promise<void>;
  bulkApprove: (ids: string[]) => Promise<void>;
  approveHighConfidence: () => Promise<number>;
  updateTransaction: (id: string, data: Record<string, unknown>) => Promise<void>;
  exportCpa: (startDate: string, endDate: string) => Promise<void>;
};

export default function useReviewQueue(): UseReviewQueueReturn {
  const queryClient = useQueryClient();
  const orgId = getActiveOrgId();
  const [activeTab, setActiveTab] = useState('pending');

  const txResponse = useApiFetch<{ data: FxTransaction[] }, void>(
    [FetchApiIds.FX_REVIEW_QUEUE, orgId],
    async () => financeTransactionsService.getReviewQueue({ page: 1, take: 500 }),
    { retry: false }
  );

  const vendorsResponse = useApiFetch<{ data: FxVendor[] }, void>(
    [FetchApiIds.FX_VENDORS, orgId],
    async () => financeVendorsService.getAll({ page: 1, take: 200 }),
    { retry: false }
  );

  const categoriesResponse = useApiFetch<FxCategory[], void>(
    [FetchApiIds.FX_CATEGORIES, orgId],
    async () => financeCategoriesService.getAll(),
    { retry: false }
  );

  const allTransactions: FxTransaction[] = useMemo(
    () => (txResponse.data as any)?.data ?? [],
    [txResponse.data]
  );
  const vendors: FxVendor[] = useMemo(
    () => (vendorsResponse.data as any)?.data ?? [],
    [vendorsResponse.data]
  );
  const categories: FxCategory[] = useMemo(
    () => (categoriesResponse.data as FxCategory[]) ?? [],
    [categoriesResponse.data]
  );

  const counts = useMemo(() => {
    const c = { all: allTransactions.length, pending: 0, reviewed: 0, approved: 0 };
    allTransactions.forEach((tx) => {
      if (tx.reviewStatus === 'pending') c.pending += 1;
      else if (tx.reviewStatus === 'reviewed') c.reviewed += 1;
      else if (tx.reviewStatus === 'approved') c.approved += 1;
    });
    return c;
  }, [allTransactions]);

  const transactions = useMemo(() => {
    if (activeTab === 'all') return allTransactions;
    return allTransactions.filter((tx) => tx.reviewStatus === activeTab);
  }, [allTransactions, activeTab]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries(FetchApiIds.FX_REVIEW_QUEUE);
    queryClient.invalidateQueries(FetchApiIds.FX_TRANSACTIONS);
  }, [queryClient]);

  const approveOne = useCallback(async (id: string) => {
    await financeTransactionsService.updateReviewStatus(id, 'approved');
    invalidate();
  }, [invalidate]);

  const markReviewed = useCallback(async (id: string) => {
    await financeTransactionsService.updateReviewStatus(id, 'reviewed');
    invalidate();
  }, [invalidate]);

  const bulkApprove = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    await financeTransactionsService.bulkUpdateReviewStatus(ids, 'approved');
    invalidate();
  }, [invalidate]);

  const approveHighConfidence = useCallback(async (): Promise<number> => {
    const count = await financeTransactionsService.approveHighConfidence(0.9);
    invalidate();
    return count;
  }, [invalidate]);

  const updateTransaction = useCallback(async (id: string, data: Record<string, unknown>) => {
    const tx = allTransactions.find((t) => t.id === id);
    const shouldSetReviewed = !tx || tx.reviewStatus === 'pending';
    await financeTransactionsService.update(id, {
      ...data,
      ...(shouldSetReviewed ? { reviewStatus: 'reviewed' as FxReviewStatus } : {}),
    });
    invalidate();
  }, [invalidate, allTransactions]);

  const exportCpa = useCallback(async (startDate: string, endDate: string) => {
    const data = await financeTransactionsService.exportForCpa({ startDate, endDate });
    const csv = generateCsv(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `trio-financex-${startDate}-to-${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  return {
    transactions,
    vendors,
    categories,
    isLoading: txResponse.isLoading,
    counts,
    activeTab,
    setActiveTab,
    approveOne,
    markReviewed,
    bulkApprove,
    approveHighConfidence,
    updateTransaction,
    exportCpa,
  };
}
